import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrders } from "@/hooks/useOrders";
import { useWallet, clearWalletCache } from "@/hooks/useWallet";
import { supabase } from "@/lib/supabase";
import {
  TIER_LIST,
  getMembershipLabel,
  getMembershipStyle,
  canAccessVIP,
} from "@/lib/membership";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

/* ── Sidebar Nav Item ─────────────────────────────── */
function SidebarItem({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: string;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm transition-all whitespace-nowrap ${
        active
          ? "bg-rose-50 text-rose-800 font-medium"
          : "text-stone-600 hover:bg-stone-50 hover:text-stone-800"
      }`}
    >
      <i className={`${icon} text-base`} />
      {label}
    </Link>
  );
}

/* ── Section: My Subscriptions ────────────────────── */
function SubscriptionsSection() {
  const { orders, loading } = useOrders();
  const active = orders.filter((o) => o.status === "active" || o.status === "進行中");
  const completed = orders.filter((o) => o.status === "completed");

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="總認購數" value={orders.length.toString()} accent="text-stone-800" />
        <StatCard label="進行中" value={active.length.toString()} accent="text-amber-600" />
        <StatCard label="已完成" value={completed.length.toString()} accent="text-emerald-600" />
        <StatCard
          label="總收益"
          value={`NT$ ${completed.reduce((s, o) => s + Math.round(((o.profit ?? 0) + (o.extra_profit ?? 0)) * (o.quantity ?? 1)), 0).toLocaleString()}`}
          accent="text-emerald-600"
        />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-stone-800">認購明細</h3>
          <span className="font-body text-[11px] text-stone-400">
            {orders.length} 筆記錄
          </span>
        </div>
        <div className="divide-y divide-stone-100">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <i className="ri-loader-4-line text-2xl text-stone-300 animate-spin" />
            </div>
          ) : orders.length > 0 ? (
            orders.map((order) => {
              const isCompleted = order.status === "completed";
              const progress = isCompleted ? 100 : (order.completion_percentage ?? 0);
              return (
                <div
                  key={order.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
                      <i className="ri-file-list-3-line text-stone-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-medium text-stone-800 truncate">
                        {order.product_name || "未知貨源"}
                      </p>
                      <p className="font-body text-[11px] text-stone-400 mt-0.5">
                        {order.created_at?.slice(0, 10) || "--"} · NT$ {order.amount.toLocaleString()}
                        {order.quantity && order.quantity > 1 ? ` × ${order.quantity}` : ""}
                      </p>
                      {/* Progress bar — same as orders/page.tsx */}
                      <div className="mt-2 max-w-[220px]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-body text-[10px] font-medium text-stone-500">
                            認購完成度
                          </span>
                          <span className="font-body text-[10px] font-bold text-stone-700">
                            {progress}%
                          </span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-[#E5E7EB] overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-[width] duration-500 ease-out ${isCompleted ? "bg-emerald-500" : "bg-[#1e3a5f]"}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-0.5">
                          <span className="font-body text-[9px] text-stone-400">0%</span>
                          <span className="font-body text-[9px] text-stone-400">100%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span
                      className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full ${
                        order.status === "active" || order.status === "進行中"
                          ? "bg-amber-50 text-amber-700 border border-amber-100"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                      }`}
                    >
                      {order.status === "active" || order.status === "進行中" ? "進行中" : "已完成"}
                    </span>
                    <span className="font-body text-xs font-semibold text-emerald-600">
                      +{order.return_rate}%
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="font-body text-sm text-stone-400">尚無認購記錄</p>
              <Link
                to="/shop"
                className="inline-block mt-3 font-body text-xs text-rose-700 hover:underline"
              >
                前往認購貨源
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Section: Wallet ──────────────────────────────── */
function WalletSection() {
  const { user, profile } = useAuth();
  const { wallet } = useWallet();

  // ── Withdrawal modal state ──
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawPassword, setWithdrawPassword] = useState("");
  const [withdrawStep, setWithdrawStep] = useState<"amount" | "password">("amount");
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawSuccess, setWithdrawSuccess] = useState("");
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);

  const balance = wallet?.balance ?? 0;
  const earnings = wallet?.total_earnings ?? 0;
  const pendingProfit = wallet?.pending_profits ?? 0;

  const isVerified =
    profile?.verification_status === "approved" || profile?.is_verified === true;
  const hasBankInfo = !!(profile?.bank_name && profile?.bank_account);

  const openWithdrawModal = () => {
    if (!isVerified) return;
    setWithdrawAmount("");
    setWithdrawPassword("");
    setWithdrawStep("amount");
    setWithdrawError("");
    setWithdrawSuccess("");
    setShowWithdrawModal(true);
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setWithdrawAmount("");
    setWithdrawPassword("");
    setWithdrawStep("amount");
    setWithdrawError("");
    setWithdrawSuccess("");
    setSubmittingWithdraw(false);
  };

  const handleWithdrawAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setWithdrawAmount(val);
    setWithdrawError("");
  };

  const handleWithdrawPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);
    setWithdrawPassword(val);
    setWithdrawError("");
  };

  const proceedToPassword = () => {
    const num = parseInt(withdrawAmount, 10);
    if (!withdrawAmount || num <= 0) {
      setWithdrawError("請輸入有效的提款金額");
      return;
    }
    if (num > balance) {
      setWithdrawError("提款金額不可超過可用餘額");
      return;
    }
    if (!hasBankInfo) {
      setWithdrawError("尚未設定銀行帳戶資訊，請先至提款中心完成綁定");
      return;
    }
    setWithdrawStep("password");
    setWithdrawError("");
  };

  const submitWithdrawal = async () => {
    if (!withdrawPassword || withdrawPassword.length < 4) {
      setWithdrawError("請輸入提款密碼");
      return;
    }
    if (!user) return;

    setSubmittingWithdraw(true);
    setWithdrawError("");

    try {
      const { data: profileData, error: profileErr } = await supabase
        .from("profiles")
        .select("withdraw_password")
        .eq("id", user.id)
        .maybeSingle();

      if (profileErr) {
        setWithdrawError("驗證失敗，請重試");
        setSubmittingWithdraw(false);
        return;
      }

      if (profileData?.withdraw_password !== withdrawPassword) {
        setWithdrawError("提款密碼錯誤，請重新輸入");
        setSubmittingWithdraw(false);
        return;
      }

      const num = parseInt(withdrawAmount, 10);
      const { error: wdErr } = await supabase.from("withdrawals").insert({
        user_id: user.id,
        amount: num,
        status: "pending",
        type: "withdrawal",
        bank_name: profile?.bank_name || null,
        bank_account: profile?.bank_account || null,
        branch_code: profile?.branch_code || null,
        account_holder: profile?.account_holder || null,
        user_name: profile?.name || profile?.full_name || null,
      });

      if (wdErr) {
        console.error("[submitWithdrawal] insert error:", wdErr);
        setWithdrawError(`提款申請失敗：${wdErr.message}`);
        setSubmittingWithdraw(false);
        return;
      }

      if (user.id) {
        clearWalletCache(user.id);
      }
      window.dispatchEvent(new Event("wallet:refresh"));

      setWithdrawSuccess("提現申請已送出，等待審核中！");
      setTimeout(() => {
        closeWithdrawModal();
      }, 2000);
    } catch (err) {
      console.error("[submitWithdrawal] exception:", err);
      setWithdrawError("提款過程發生錯誤，請重試");
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Three stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center mb-3">
            <i className="ri-wallet-3-line text-rose-600 text-lg" />
          </div>
          <p className="font-body text-xs text-stone-400">可用餘額</p>
          <p className="font-display text-2xl font-bold text-stone-800 mt-1">
            NT$ {balance.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
            <i className="ri-coins-line text-emerald-600 text-lg" />
          </div>
          <p className="font-body text-xs text-stone-400">累計收益</p>
          <p className="font-display text-2xl font-bold text-emerald-600 mt-1">
            NT$ {earnings.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
            <i className="ri-time-line text-amber-600 text-lg" />
          </div>
          <p className="font-body text-xs text-stone-400">待結算利潤</p>
          <p className="font-display text-2xl font-bold text-amber-600 mt-1">
            NT$ {pendingProfit.toLocaleString()}
          </p>
        </div>
      </div>

      {/* ── Withdrawal Application ── */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-stone-800">
            <i className="ri-money-cny-circle-line text-rose-700 mr-1.5" />
            提現申請
          </h3>
          <span className="font-body text-[11px] text-stone-400">
            可用餘額 NT$ {balance.toLocaleString()}
          </span>
        </div>
        <div className="px-6 py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <p className="font-body text-sm text-stone-500">
                {isVerified
                  ? hasBankInfo
                    ? "實名認證已通過，可申請提現"
                    : "實名認證已通過，請先綁定銀行帳戶"
                  : "需完成實名認證後方可提現"}
              </p>
            </div>
            <button
              onClick={openWithdrawModal}
              disabled={!isVerified || submittingWithdraw}
              className={`font-body text-sm font-semibold px-6 py-2.5 rounded-full whitespace-nowrap transition-colors shrink-0 ${
                isVerified
                  ? "bg-stone-800 text-white hover:bg-stone-900 cursor-pointer"
                  : "bg-stone-200 text-stone-400 cursor-not-allowed"
              }`}
            >
              申請提現
            </button>
          </div>

          {!isVerified && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mt-4">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <p className="font-body text-xs text-red-600">
                請先完成實名認證
              </p>
            </div>
          )}

          {isVerified && !hasBankInfo && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-amber-600 text-sm" />
              </div>
              <p className="font-body text-xs text-amber-700">
                尚未綁定銀行帳戶，請先至提款中心完成綁定後方可提現
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Withdrawal Modal ── */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeWithdrawModal}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-stone-800">
                <i className="ri-money-cny-circle-line text-rose-700 mr-2" />
                {withdrawStep === "amount" ? "提現金額" : "安全驗證"}
              </h3>
              <button
                onClick={closeWithdrawModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-stone-400" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-4">
              {withdrawStep === "amount" ? (
                <>
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      提現金額（NT$）
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={withdrawAmount}
                      onChange={handleWithdrawAmountChange}
                      placeholder="請輸入提現金額"
                      autoFocus
                      className="w-full font-body text-lg px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300 text-center"
                    />
                    <p className="font-body text-xs text-stone-400 mt-1.5 text-center">
                      可用餘額：NT$ {balance.toLocaleString()}
                    </p>
                  </div>

                  {withdrawError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-500 text-sm mt-0.5" />
                      <p className="font-body text-xs text-red-600">{withdrawError}</p>
                    </div>
                  )}

                  <button
                    onClick={proceedToPassword}
                    className="w-full font-body text-sm font-semibold px-5 py-3 rounded-full bg-stone-800 text-white hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    下一步：輸入提款密碼
                  </button>
                </>
              ) : (
                <>
                  <div>
                    <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
                      提款密碼
                    </label>
                    <input
                      type="password"
                      value={withdrawPassword}
                      onChange={handleWithdrawPasswordChange}
                      placeholder="請輸入提款密碼"
                      autoFocus
                      maxLength={6}
                      className="w-full font-body text-lg tracking-[0.3em] px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all text-center placeholder:tracking-normal placeholder:text-stone-300"
                    />
                    <p className="font-body text-xs text-stone-400 mt-1.5 text-center">
                      提現金額：NT$ {parseInt(withdrawAmount || "0", 10).toLocaleString()}
                    </p>
                  </div>

                  {withdrawError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-500 text-sm mt-0.5" />
                      <p className="font-body text-xs text-red-600">{withdrawError}</p>
                    </div>
                  )}

                  {withdrawSuccess && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-600 text-sm mt-0.5" />
                      <p className="font-body text-xs text-emerald-700">{withdrawSuccess}</p>
                    </div>
                  )}

                  <button
                    onClick={submitWithdrawal}
                    disabled={submittingWithdraw || withdrawPassword.length < 4}
                    className="w-full font-body text-sm font-semibold px-5 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {submittingWithdraw ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        處理中...
                      </span>
                    ) : (
                      "確認提現"
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setWithdrawStep("amount");
                      setWithdrawError("");
                      setWithdrawPassword("");
                    }}
                    className="w-full font-body text-sm font-medium px-5 py-2.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    返回修改金額
                  </button>
                </>
              )}

              <button
                onClick={closeWithdrawModal}
                className="w-full font-body text-sm font-medium px-5 py-2.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Section: VIP Status ──────────────────────────── */
function VIPStatusSection() {
  const { profile } = useAuth();
  const { orders } = useOrders();
  const isVIP = canAccessVIP(profile?.membership_level);
  const completedCount = orders.filter((o) => o.status === "completed").length;

  const currentTierIndex = TIER_LIST.findIndex(
    (t, i) =>
      completedCount >= t.minOrders &&
      (i === TIER_LIST.length - 1 || completedCount < TIER_LIST[i + 1].minOrders)
  );
  const currentTier = TIER_LIST[Math.max(0, currentTierIndex)];
  const nextTier = TIER_LIST[currentTierIndex + 1];
  const progressToNext = nextTier
    ? Math.min(100, Math.round((completedCount / nextTier.minOrders) * 100))
    : 100;

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex items-center gap-4 mb-4">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              isVIP ? "bg-amber-100" : "bg-stone-100"
            }`}
          >
            <i
              className={`text-2xl ${
                isVIP ? "ri-vip-crown-line text-amber-600" : "ri-user-line text-stone-500"
              }`}
            />
          </div>
          <div>
            <p className="font-body text-xs text-stone-400">當前等級</p>
            <p className="font-display text-xl font-bold text-stone-800">
              {currentTier.name}
            </p>
            <p className="font-body text-xs text-stone-400 mt-0.5">
              已完成 {completedCount} 筆認購
            </p>
          </div>
        </div>

        <div className="mb-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-body text-xs text-stone-500">等級進度</span>
            <span className="font-body text-xs text-stone-500">
              {nextTier
                ? `${completedCount} / ${nextTier.minOrders} 筆`
                : "已達最高等級"}
            </span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-stone-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500 transition-all duration-700"
              style={{ width: `${progressToNext}%` }}
            />
          </div>
        </div>

        {!isVIP && nextTier && (
          <p className="font-body text-xs text-stone-400">
            再完成 {nextTier.minOrders - completedCount} 筆認購即可升級為 {nextTier.name}
          </p>
        )}
      </div>

      {/* Tier perks */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100">
          <h3 className="font-display text-base font-bold text-stone-800">
            等級權益一覽
          </h3>
        </div>
        <div className="divide-y divide-stone-100">
          {TIER_LIST.map((tier, i) => (
            <div
              key={tier.level}
              className={`px-6 py-4 flex items-start gap-4 ${
                i === currentTierIndex ? "bg-amber-50/50" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  i === currentTierIndex
                    ? "bg-amber-500 text-white"
                    : i < currentTierIndex
                    ? "bg-stone-200 text-stone-500"
                    : "bg-stone-100 text-stone-300"
                }`}
              >
                <span className="font-body text-xs font-bold">{i + 1}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-body text-sm font-semibold text-stone-800">
                    {tier.name}
                  </p>
                  {i === currentTierIndex && (
                    <span className="font-body text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                      目前
                    </span>
                  )}
                </div>
                <p className="font-body text-[11px] text-stone-400 mt-0.5">
                  需要完成 {tier.minOrders} 筆不同件商品的認購
                </p>
                <ul className="flex flex-wrap gap-2 mt-2">
                  {tier.perks.map((perk) => (
                    <li
                      key={perk}
                      className="font-body text-[10px] text-stone-500 bg-stone-100 px-2 py-1 rounded-md"
                    >
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Helper: StatCard ───────────────────────────── */
function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
      <p className={`font-display text-xl md:text-2xl font-bold ${accent}`}>{value}</p>
      <p className="font-body text-xs text-stone-500 mt-1">{label}</p>
    </div>
  );
}

/* ── Main Dashboard Page ──────────────────────────── */
export default function DashboardPage() {
  const location = useLocation();
  const { profile, isLoggedIn } = useAuth();

  const tab = location.hash.replace("#", "") || "subscriptions";

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <main className="pt-24 px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-stone-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-line text-2xl text-stone-400" />
            </div>
            <h2 className="font-display text-xl font-bold text-stone-800 mb-2">
              請先登入
            </h2>
            <p className="font-body text-sm text-stone-500 mb-6">
              登入後即可進入認購中心
            </p>
            <a
              href="/?showLogin=1"
              className="inline-block font-body text-sm font-semibold px-6 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors"
            >
              前往登入
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const userName = profile?.name || "會員";
  const initials = userName.slice(0, 2).toUpperCase();
  const levelLabel = getMembershipLabel(profile?.membership_level);
  const levelStyle = getMembershipStyle(profile?.membership_level);
  const isVIP = canAccessVIP(profile?.membership_level);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Sidebar */}
              <aside className="w-full md:w-60 shrink-0">
                <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                  <div className="px-5 py-5 border-b border-stone-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-rose-800 flex items-center justify-center shrink-0">
                        <span className="font-display text-sm font-bold text-white">
                          {initials}
                        </span>
                      </div>
                      <div>
                        <p className="font-body text-sm font-semibold text-stone-800">
                          {userName}
                        </p>
                        <p className="font-body text-[10px] text-stone-400">
                          {levelLabel}
                        </p>
                      </div>
                    </div>
                  </div>

                  <nav className="flex flex-col p-2">
                    <SidebarItem
                      to="/dashboard#subscriptions"
                      icon="ri-file-list-3-line"
                      label="我的認購"
                      active={tab === "subscriptions"}
                    />
                    <SidebarItem
                      to="/dashboard#wallet"
                      icon="ri-wallet-3-line"
                      label="我的錢包"
                      active={tab === "wallet"}
                    />
                    <SidebarItem
                      to="/dashboard#vip"
                      icon="ri-vip-crown-line"
                      label="VIP 狀態"
                      active={tab === "vip"}
                    />
                    <div className="h-px bg-stone-100 my-2 mx-2" />
                    <SidebarItem
                      to="/profile"
                      icon="ri-user-settings-line"
                      label="會員資料"
                      active={false}
                    />
                    <SidebarItem
                      to="/shop"
                      icon="ri-shopping-bag-line"
                      label="瀏覽貨源"
                      active={false}
                    />
                  </nav>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="mb-6">
                  <h1 className="font-display text-xl md:text-2xl font-bold text-stone-800">
                    {tab === "subscriptions" && "我的認購"}
                    {tab === "wallet" && "我的錢包"}
                    {tab === "vip" && "VIP 狀態"}
                  </h1>
                  <p className="font-body text-sm text-stone-500 mt-1">
                    {tab === "subscriptions" && "查看所有認購明細與收益狀態"}
                    {tab === "wallet" && "管理帳戶餘額與提現"}
                    {tab === "vip" && "會員等級進度與權益"}
                  </p>
                </div>

                {tab === "subscriptions" && <SubscriptionsSection />}
                {tab === "wallet" && <WalletSection />}
                {tab === "vip" && <VIPStatusSection />}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}